// File: src/services/progressSync.ts
// Progress synchronization layer for espforsports.com
//
// Ports the original Vanilla JS `progress-sync.js` to TypeScript while
// preserving every behavioral guarantee:
//   • the no-downgrade guard on optimistic Firestore writes,
//   • the reset-wins merge priority,
//   • the anon → user IDB migration that DELETES anon keys after copy,
//   • the `progressSynced` DOM CustomEvent fired on remote merges.
//
// Lifecycle (real-time listeners + migration) is owned by the auth service
// in Session 3 — this file only exposes the imperative entry points.

import type { User } from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  setDoc,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';

import { auth, db } from '../lib/firebase';
import {
  STORE_META,
  STORE_PROGRESS,
  addUnitToIndex,
  idbDel,
  idbGet,
  idbKeys,
  idbSet,
  progressIndexKey,
  progressKey,
} from '../lib/idb-wrapper';
import { SyncQueue, type ProcessResult, type QueueItem } from '../lib/sync-queue';
import type { SectionProgress, UnitProgress } from '../types/progress';

// ── Helpers ──────────────────────────────────────────────────────────────────

const nowMs = (): number => Date.now();

function safeNum(x: unknown, d = 0): number {
  const n = Number(x);
  return Number.isFinite(n) ? n : d;
}

/**
 * Normalize raw section progress to the canonical SectionProgress shape with
 * defaults filled in. Used both on read (to clean up data from Firestore/IDB)
 * and on write (to ensure consistent shape goes into IDB/Firestore).
 *
 * Legacy fix: score/maxScore fall back to completed/total respectively when
 * absent, since some early data didn't carry them explicitly.
 */
function sanitizeSectionProgress(p: Partial<SectionProgress> = {}): SectionProgress {
  let lastUpdatedMs = safeNum(p.lastUpdatedMs, 0);

  // If lastUpdatedMs missing but lastUpdated string exists, recover from string.
  if (!lastUpdatedMs && typeof p.lastUpdated === 'string') {
    const t = Date.parse(p.lastUpdated);
    if (Number.isFinite(t)) lastUpdatedMs = t;
  }

  const out: SectionProgress = {
    completed: safeNum(p.completed, 0),
    total: safeNum(p.total, 0),
    score: safeNum(p.score, safeNum(p.completed, 0)),
    maxScore: safeNum(p.maxScore, safeNum(p.total, 0)),
    percentage: safeNum(p.percentage, 0),
    version: safeNum(p.version, 0),
    pendingSync: !!p.pendingSync,
    lastUpdatedMs,
    lastUpdated:
      typeof p.lastUpdated === 'string'
        ? p.lastUpdated
        : new Date(lastUpdatedMs).toISOString(),
  };

  if (typeof p.completedFlag === 'boolean') out.completedFlag = p.completedFlag;
  // resetAt: ms timestamp written only by explicit user resets — beats completedFlag.
  if (safeNum(p.resetAt, 0) > 0) out.resetAt = safeNum(p.resetAt, 0);
  return out;
}

/**
 * Validate a saveProgress input and fill in defaults non-destructively.
 * Returns a new object — does not mutate caller's input.
 */
function validateAndFillInput(
  progress: Partial<SectionProgress> | null | undefined
): Partial<SectionProgress> {
  if (!progress || typeof progress !== 'object') {
    throw new Error('Invalid progress payload');
  }
  const out: Partial<SectionProgress> = { ...progress };

  if (out.score === undefined || out.score === null) {
    out.score = out.completed !== undefined ? out.completed : 0;
  }
  if (out.maxScore === undefined || out.maxScore === null) {
    out.maxScore = out.total !== undefined ? out.total : 0;
  }
  if (!Number.isFinite(Number(out.score))) out.score = 0;
  if (!Number.isFinite(Number(out.maxScore))) out.maxScore = 0;
  return out;
}

/**
 * Merge two SectionProgress values (typically local IDB and remote Firestore)
 * using the project's authoritative priority order. PRESERVE EXACTLY — every
 * branch here covers a real-world conflict scenario from the old codebase.
 *
 * MERGE PRIORITY (most → least important):
 *   0. RESET WINS — checked before everything else, even completedFlag.
 *      When a user explicitly resets a section, resetAt is stamped with
 *      Date.now(). If LOCAL was reset more recently than REMOTE was last
 *      written, the reset wins even if REMOTE has completedFlag:true.
 *      Without this, resetting a 100% section would be immediately un-done
 *      by the Firestore listener echoing the old 100% data back.
 *   1. completedFlag: a fully-completed section beats an incomplete one.
 *      Without this, a device that finishes 3/3 AFTER another device wrote
 *      1/3 could get overwritten if the 1/3 write had a higher version
 *      number (version is a device-local counter, not a quality indicator).
 *   2. percentage: higher score wins.
 *   3. version: tiebreaker when scores are identical.
 *   4. lastUpdatedMs: final tiebreaker.
 */
function mergeSection(
  localP: Partial<SectionProgress>,
  remoteP: Partial<SectionProgress>
): SectionProgress {
  const L = sanitizeSectionProgress(localP);
  const R = sanitizeSectionProgress(remoteP);

  // 0. RESET WINS
  const localResetAt = safeNum(L.resetAt, 0);
  const remoteResetAt = safeNum(R.resetAt, 0);
  if (localResetAt > 0 && localResetAt >= safeNum(R.lastUpdatedMs, 0)) {
    return { ...R, ...L, pendingSync: false };
  }
  if (remoteResetAt > 0 && remoteResetAt >= safeNum(L.lastUpdatedMs, 0)) {
    return { ...L, ...R, pendingSync: false };
  }

  // 1. completedFlag
  if (R.completedFlag && !L.completedFlag) return { ...L, ...R, pendingSync: false };
  if (L.completedFlag && !R.completedFlag) return L;

  // 2. percentage
  if (R.percentage > L.percentage) return { ...L, ...R, pendingSync: false };
  if (R.percentage < L.percentage) return L;

  // 3. version (tiebreaker)
  if (R.version > L.version) return { ...L, ...R, pendingSync: false };
  if (R.version < L.version) return L;

  // 4. lastUpdatedMs (final tiebreaker)
  if (R.lastUpdatedMs >= L.lastUpdatedMs) return { ...L, ...R, pendingSync: false };
  return L;
}

// ── Public types ─────────────────────────────────────────────────────────────

export interface SaveProgressOptions {
  /** Skip the no-downgrade guard. Used by intentional reset operations. */
  force?: boolean;
}

export type SaveProgressResult =
  | { ok: true; localOnly?: boolean; skipped?: 'would_downgrade' }
  | { ok: false; queued: true; error: unknown };

export interface MigrationResult {
  migrated: number;
}

// ── Class ────────────────────────────────────────────────────────────────────

export class ProgressSync {
  private readonly queue: SyncQueue;
  private unsubscribe: Unsubscribe | null = null;
  private lastListenerUid: string | null = null;

  constructor() {
    this.queue = new SyncQueue();
  }

  get currentUid(): string | null {
    return auth?.currentUser?.uid ?? null;
  }

  private _uidOrAnon(): string {
    return this.currentUid || 'anon';
  }

  // ── Read ─────────────────────────────────────────────────────────────────

  async loadProgress(unitId: string): Promise<UnitProgress> {
    const uidOrAnon = this._uidOrAnon();
    const k = progressKey(uidOrAnon, unitId);

    // 1) Try local
    const local = (await idbGet(k, STORE_PROGRESS)) as UnitProgress | undefined;
    if (local && typeof local === 'object') return local;

    // 2) If logged in, pull from cloud
    if (this.currentUid) {
      try {
        const ref = doc(db, 'users', this.currentUid, 'progress', unitId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = (snap.data() || {}) as DocumentData;
          const normalized = this._normalizeUnitDoc(data);
          await idbSet(k, normalized, STORE_PROGRESS);
          await addUnitToIndex(uidOrAnon, unitId);
          return normalized;
        }
      } catch (e) {
        console.warn('Cloud load failed:', e);
      }
    }

    // 3) Empty default
    const empty: UnitProgress = {};
    await idbSet(k, empty, STORE_PROGRESS);
    await addUnitToIndex(uidOrAnon, unitId);
    return empty;
  }

  private _normalizeUnitDoc(data: DocumentData | Record<string, unknown>): UnitProgress {
    const out: UnitProgress = {};
    for (const [sectionId, sectionProgress] of Object.entries(data || {})) {
      if (!sectionProgress || typeof sectionProgress !== 'object') continue;
      const cleaned = sanitizeSectionProgress(sectionProgress as Partial<SectionProgress>);
      cleaned.pendingSync = false;
      out[sectionId] = cleaned;
    }
    return out;
  }

  // ── Write ────────────────────────────────────────────────────────────────

  async saveProgress(
    unitId: string,
    sectionId: string,
    progress: Partial<SectionProgress>,
    options: SaveProgressOptions = {}
  ): Promise<SaveProgressResult> {
    const forceWrite = options.force === true;
    const validated = validateAndFillInput(progress);

    const uidOrAnon = this._uidOrAnon();
    const k = progressKey(uidOrAnon, unitId);
    const existingUnit = ((await idbGet(k, STORE_PROGRESS)) as UnitProgress | undefined) || {};
    const prevSection: Partial<SectionProgress> = existingUnit[sectionId] || {};

    // FIX #11: Use explicit >= 0 check instead of || to avoid treating version 0
    // as "missing". The || operator converts version:0 to false, causing inflation
    // where prevSection.version+1 is used even when version:0 was set deliberately.
    const incomingVersion = safeNum(validated.version, -1);
    const nextVersion =
      incomingVersion >= 0 ? incomingVersion : safeNum(prevSection.version, 0) + 1;

    const lastUpdatedMs = nowMs();
    const totalNum = safeNum(validated.total, 0);
    const completedNum = safeNum(validated.completed, 0);
    const percentage =
      validated.percentage !== undefined
        ? safeNum(validated.percentage, 0)
        : totalNum > 0
          ? (completedNum / totalNum) * 100
          : 0;

    const nextSection: SectionProgress = sanitizeSectionProgress({
      ...prevSection,
      ...validated,
      version: nextVersion,
      lastUpdatedMs,
      lastUpdated: new Date(lastUpdatedMs).toISOString(),
      percentage,
      pendingSync: true,
    });

    // Optimistic local write
    const nextUnit: UnitProgress = { ...existingUnit, [sectionId]: nextSection };
    await idbSet(k, nextUnit, STORE_PROGRESS);
    await addUnitToIndex(uidOrAnon, unitId);

    // Firestore write only if logged in
    if (!this.currentUid) return { ok: true, localOnly: true };

    try {
      const ref = doc(db, 'users', this.currentUid, 'progress', unitId);

      // NO-DOWNGRADE GUARD — re-read IDB immediately before the Firestore write.
      // Between the optimistic local write above and this point, a real-time
      // listener may have merged a BETTER remote result into IDB. If that
      // happened, writing nextSection to Firestore would overwrite the better
      // value. We skip the write in that case so the better data survives.
      // EXCEPTION: forceWrite=true bypasses this guard (used by reset operations
      // so a deliberate reset to 0% is never blocked by the guard).
      const reReadUnit = (await idbGet(k, STORE_PROGRESS)) as UnitProgress | undefined;
      const latestLocal = reReadUnit?.[sectionId];
      if (
        !forceWrite &&
        latestLocal &&
        safeNum(latestLocal.percentage, 0) > safeNum(nextSection.percentage, 0) &&
        !latestLocal.pendingSync
      ) {
        console.log(
          `[ProgressSync] Skipping Firestore write — IDB already has better data` +
            ` (${latestLocal.percentage}% > ${nextSection.percentage}%)`
        );
        return { ok: true, skipped: 'would_downgrade' };
      }

      await setDoc(
        ref,
        { [sectionId]: { ...nextSection, pendingSync: false } },
        { merge: true }
      );

      // Update local pendingSync false
      const synced: SectionProgress = { ...nextSection, pendingSync: false };
      await idbSet(k, { ...nextUnit, [sectionId]: synced }, STORE_PROGRESS);
      return { ok: true };
    } catch (err) {
      // Queue for retry
      await this.queue.enqueue({
        type: 'progress_write',
        unitId,
        sectionId,
        payload: { [sectionId]: nextSection } as Record<string, unknown>,
      });
      return { ok: false, queued: true, error: err };
    }
  }

  // ── Sync queue ───────────────────────────────────────────────────────────

  /** Compatibility alias used by the auth flow on logout. */
  async syncPendingProgress(): Promise<ProcessResult> {
    return this.flushQueue();
  }

  /** Compatibility alias. */
  async flushQueue(): Promise<ProcessResult> {
    return this.retryQueueItems();
  }

  async retryQueueItems(): Promise<ProcessResult> {
    if (!this.currentUid) {
      return { processed: 0, done: 0, failed: 0, remaining: 0 };
    }

    const uidOrAnon = this._uidOrAnon();

    return this.queue.process(
      async (item: QueueItem) => {
        if (item.type !== 'progress_write') return;

        const { unitId, sectionId } = item;
        if (!unitId || !sectionId) {
          throw new Error('Queue item missing unitId or sectionId');
        }

        const payload = (item.payload || {}) as Record<string, Partial<SectionProgress> | undefined>;
        const sectionProgress =
          payload[sectionId] ||
          (payload.progress as Partial<SectionProgress> | undefined);
        if (!sectionProgress) throw new Error('Queue item missing section payload');

        // Load local to merge version safely
        const k = progressKey(uidOrAnon, unitId);
        const unitLocal = ((await idbGet(k, STORE_PROGRESS)) as UnitProgress | undefined) || {};
        const localSection: Partial<SectionProgress> = unitLocal[sectionId] || {};

        const merged = mergeSection(localSection, sectionProgress);
        const toSend: SectionProgress = {
          ...sanitizeSectionProgress(sectionProgress),
          pendingSync: false,
        };

        // currentUid is checked at top of method, but TS doesn't carry that
        // narrowing into the closure — re-assert here.
        const uid = this.currentUid;
        if (!uid) throw new Error('User signed out mid-flush');

        const ref = doc(db, 'users', uid, 'progress', unitId);
        await setDoc(ref, { [sectionId]: toSend }, { merge: true });

        // Mark local as synced
        unitLocal[sectionId] = { ...merged, pendingSync: false };
        await idbSet(k, unitLocal, STORE_PROGRESS);
      },
      { maxItems: 20 }
    );
  }

  // ── Migration (anon → user) ──────────────────────────────────────────────

  /**
   * Copy anonymous-keyed IDB progress to a freshly-signed-in user, then DELETE
   * anon entries so the same data isn't re-migrated to the next account on
   * this device. Skips Firebase anonymous users (those are reading-only).
   */
  async migrateOnLogin(user: User | null): Promise<MigrationResult> {
    if (!user || !user.uid) {
      console.warn('No user provided for migration');
      return { migrated: 0 };
    }
    if (user.isAnonymous) {
      return { migrated: 0 };
    }

    try {
      console.log('🔄 Migrating anonymous progress to user:', user.uid);

      const allKeys = await this.getAllProgressKeys();
      let migratedCount = 0;
      const anonKeysToDelete: string[] = [];

      for (const rawKey of allKeys) {
        if (typeof rawKey !== 'string' || !rawKey.startsWith('progress:anon:')) continue;
        const key: string = rawKey;

        const unitId = key.split(':')[2];
        if (!unitId) continue;

        const anonProgress = (await idbGet(key, STORE_PROGRESS)) as UnitProgress | undefined;
        if (!anonProgress || typeof anonProgress !== 'object') {
          anonKeysToDelete.push(key);
          continue;
        }

        // Only migrate if there is actual completed work — skip empty placeholders.
        const hasMeaningfulData = Object.values(anonProgress).some(
          s => s && typeof s === 'object' && (safeNum(s.completed) > 0 || safeNum(s.score) > 0)
        );
        if (!hasMeaningfulData) {
          anonKeysToDelete.push(key);
          continue;
        }

        const userKey = progressKey(user.uid, unitId);
        const userProgress =
          ((await idbGet(userKey, STORE_PROGRESS)) as UnitProgress | undefined) || {};

        const merged: UnitProgress = {};
        for (const [sectionId, anonSection] of Object.entries(anonProgress)) {
          merged[sectionId] = mergeSection(userProgress[sectionId] || {}, anonSection);
        }

        await idbSet(userKey, merged, STORE_PROGRESS);
        await addUnitToIndex(user.uid, unitId);

        // Push merged progress up to Firestore so the new account has it on
        // any other device. Skip empty sections.
        for (const [sectionId, secProgress] of Object.entries(merged)) {
          if (safeNum(secProgress.completed) > 0 || safeNum(secProgress.score) > 0) {
            await this.saveProgress(unitId, sectionId, secProgress);
          }
        }

        anonKeysToDelete.push(key);
        migratedCount++;
      }

      // DELETE anon IDB entries — critical to stop re-migration on next login.
      for (const key of anonKeysToDelete) {
        try {
          await idbDel(key, STORE_PROGRESS);
        } catch {
          /* ignore */
        }
      }
      // Clear anon unit index too
      try {
        await idbDel(progressIndexKey('anon'), STORE_META);
      } catch {
        /* ignore */
      }

      console.log(`✅ Migrated ${migratedCount} units from anonymous to user`);
      return { migrated: migratedCount };
    } catch (error) {
      console.error('❌ Migration failed:', error);
      throw error;
    }
  }

  async getAllProgressKeys(): Promise<IDBValidKey[]> {
    try {
      return await idbKeys(STORE_PROGRESS);
    } catch (e) {
      console.warn('Could not load progress keys:', e);
      return [];
    }
  }

  // ── Counts ───────────────────────────────────────────────────────────────

  async getPendingCount(unitId: string | null = null): Promise<number> {
    const uidOrAnon = this._uidOrAnon();
    if (unitId) {
      const u =
        ((await idbGet(progressKey(uidOrAnon, unitId), STORE_PROGRESS)) as UnitProgress | undefined) || {};
      return Object.values(u).filter(x => x && x.pendingSync).length;
    }
    return this.queue.count();
  }

  // ── Bulk Firestore pull (used when IDB is empty on login) ───────────────

  async fetchAndHydrateFromFirestore(): Promise<Record<string, UnitProgress>> {
    const uid = this.currentUid;
    if (!uid) return {};
    try {
      const colRef = collection(db, 'users', uid, 'progress');
      const snap = await getDocs(colRef);
      if (snap.empty) return {};

      const uidOrAnon = this._uidOrAnon();
      const result: Record<string, UnitProgress> = {};

      for (const docSnap of snap.docs) {
        const unitId = docSnap.id;
        const data = (docSnap.data() || {}) as DocumentData;
        const remoteUnit = this._normalizeUnitDoc(data);
        const k = progressKey(uidOrAnon, unitId);
        const localUnit =
          ((await idbGet(k, STORE_PROGRESS)) as UnitProgress | undefined) || {};

        const merged: UnitProgress = { ...localUnit };
        for (const [sectionId, remoteSection] of Object.entries(remoteUnit)) {
          const localSection = localUnit[sectionId] || {};
          merged[sectionId] = mergeSection(localSection, remoteSection);
        }

        await idbSet(k, merged, STORE_PROGRESS);
        await addUnitToIndex(uidOrAnon, unitId);
        result[unitId] = merged;
      }

      return result;
    } catch (e) {
      console.warn('fetchAndHydrateFromFirestore failed:', e);
      return {};
    }
  }

  // ── Real-time listeners (Session 3 owns the lifecycle) ───────────────────

  enableRealTimeListeners(): void {
    const uid = this.currentUid;
    if (!uid) {
      console.warn('Cannot enable listeners: no user logged in');
      return;
    }
    if (this.unsubscribe && this.lastListenerUid === uid) {
      console.log('Listeners already active for this user');
      return;
    }

    // Disable previous listener (different uid case).
    this.disableRealTimeListeners();
    this.lastListenerUid = uid;

    try {
      const colRef = collection(db, 'users', uid, 'progress');
      this.unsubscribe = onSnapshot(
        colRef,
        async (snap) => {
          const uidOrAnon = this._uidOrAnon();
          for (const change of snap.docChanges()) {
            if (change.type === 'removed') continue;
            const unitId = change.doc.id;
            const data = (change.doc.data() || {}) as DocumentData;
            const remoteUnit = this._normalizeUnitDoc(data);

            const k = progressKey(uidOrAnon, unitId);
            const localUnit =
              ((await idbGet(k, STORE_PROGRESS)) as UnitProgress | undefined) || {};

            const merged: UnitProgress = { ...localUnit };
            for (const [sectionId, remoteSection] of Object.entries(remoteUnit)) {
              const localSection = localUnit[sectionId] || {};
              merged[sectionId] = mergeSection(localSection, remoteSection);
            }

            await idbSet(k, merged, STORE_PROGRESS);
            await addUnitToIndex(uidOrAnon, unitId);

            document.dispatchEvent(
              new CustomEvent('progressSynced', { detail: { unitId } })
            );
          }
        },
        (err) => {
          console.warn('ProgressSync listener error:', err);
        }
      );

      console.log('✅ Real-time listeners enabled');
    } catch (error) {
      console.error('❌ Failed to enable listeners:', error);
    }
  }

  disableRealTimeListeners(): void {
    try {
      if (this.unsubscribe) {
        this.unsubscribe();
        console.log('✅ Real-time listeners disabled');
      }
    } catch (e) {
      console.warn('Error disabling listeners:', e);
    }
    this.unsubscribe = null;
    this.lastListenerUid = null;
  }
}

// Singleton — most consumers should import this. The class is exported above
// for testability and for the rare case that needs an isolated instance.
export const progressSync = new ProgressSync();